import {
  TextInput,
  Select,
  Checkbox,
  Paper,
  Button,
  Group,
  Text,
  Table,
  ActionIcon,
} from '@mantine/core'
import { IconPlus, IconTrash } from '@tabler/icons-react'
import type { FamilyHistoryRow } from '../formData'

type Props = {
  title?: string
  familyHistory: FamilyHistoryRow[]
  onFamilyHistoryChange: (rows: FamilyHistoryRow[]) => void
  noFamilyHistory: boolean
  onNoFamilyHistoryChange: (checked: boolean) => void
  patientIsAdopted: boolean
  onPatientIsAdoptedChange: (checked: boolean) => void
  noHistoryLabel?: string
  conditionPlaceholder?: string
}

export const FamilyHistorySection = ({
  title = 'FAMILY HISTORY',
  familyHistory,
  onFamilyHistoryChange,
  noFamilyHistory,
  onNoFamilyHistoryChange,
  patientIsAdopted,
  onPatientIsAdoptedChange,
  noHistoryLabel = 'No known family history of disease',
  conditionPlaceholder = 'e.g. Breast cancer, HCM',
}: Props) => {
  const addRow = () => {
    onFamilyHistoryChange([
      ...familyHistory,
      {
        relationship: '',
        maternalPaternal: '',
        condition: '',
        ageAtDiagnosis: '',
        additionalDetails: '',
      },
    ])
  }

  const updateRow = (index: number, field: keyof FamilyHistoryRow, value: string) => {
    const rows = [...familyHistory]
    rows[index] = { ...rows[index], [field]: value }
    onFamilyHistoryChange(rows)
  }

  const removeRow = (index: number) => {
    onFamilyHistoryChange(familyHistory.filter((_, i) => i !== index))
  }

  return (
    <Paper p="md" withBorder>
      <Text fw={600} size="lg" mb="sm" className="text-[#0097CE]">
        {title}
      </Text>
      <Group gap="md" mb="sm">
        <Checkbox
          label={noHistoryLabel}
          checked={noFamilyHistory}
          onChange={(e) => onNoFamilyHistoryChange(e.target.checked)}
        />
        <Checkbox
          label="Patient is adopted"
          checked={patientIsAdopted}
          onChange={(e) => onPatientIsAdoptedChange(e.target.checked)}
        />
      </Group>

      {!noFamilyHistory && (
        <>
          <Table withTableBorder withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Relative&apos;s Relationship</Table.Th>
                <Table.Th>Maternal/Paternal</Table.Th>
                <Table.Th>Diagnosed Condition</Table.Th>
                <Table.Th>Age at Diagnosis</Table.Th>
                <Table.Th>Additional Details</Table.Th>
                <Table.Th w={40} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {familyHistory.map((row, i) => (
                <Table.Tr key={i}>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      variant="unstyled"
                      placeholder="e.g. Mother, Sister"
                      value={row.relationship}
                      onChange={(e) => updateRow(i, 'relationship', e.target.value)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Select
                      size="xs"
                      variant="unstyled"
                      placeholder="Select"
                      data={['Maternal', 'Paternal']}
                      value={row.maternalPaternal}
                      onChange={(v) => updateRow(i, 'maternalPaternal', v || '')}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      variant="unstyled"
                      placeholder={conditionPlaceholder}
                      value={row.condition}
                      onChange={(e) => updateRow(i, 'condition', e.target.value)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      variant="unstyled"
                      placeholder="Age"
                      value={row.ageAtDiagnosis}
                      onChange={(e) => updateRow(i, 'ageAtDiagnosis', e.target.value)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      variant="unstyled"
                      placeholder="Details..."
                      value={row.additionalDetails}
                      onChange={(e) => updateRow(i, 'additionalDetails', e.target.value)}
                    />
                  </Table.Td>
                  <Table.Td>
                    <ActionIcon
                      size="sm"
                      variant="subtle"
                      color="red"
                      onClick={() => removeRow(i)}
                      disabled={familyHistory.length <= 1}
                    >
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
          <Button
            variant="light"
            size="xs"
            leftSection={<IconPlus size={14} />}
            onClick={addRow}
            mt="xs"
          >
            Add Relative
          </Button>
        </>
      )}
    </Paper>
  )
}
